import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { AuthProvider } from 'react-oidc-context';
import { HelmetProvider } from 'react-helmet-async';
import { consumePostLoginDestination } from './lib/postLoginRedirect';
import * as Sentry from '@sentry/react';

// Initialize Sentry for error tracking
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_ENVIRONMENT || 'development',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,
    // Session Replay
    replaysSessionSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 0,
    replaysOnErrorSampleRate: 1.0,
    // Ignore common errors
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
    ],
    beforeSend(event, hint) {
      // Filter out low-priority errors
      const error = hint.originalException;
      if (error && typeof error === 'object' && 'message' in error) {
        const message = String(error.message).toLowerCase();
        // Don't send canceled requests
        if (message.includes('canceled') || message.includes('aborted')) {
          return null;
        }
      }
      return event;
    },
  });
}

// AWS Cognito configuration
const cognitoAuthConfig = {
  authority: process.env.REACT_APP_COGNITO_AUTHORITY || '',
  client_id: process.env.REACT_APP_COGNITO_CLIENT_ID || '',
  redirect_uri: process.env.REACT_APP_COGNITO_REDIRECT_URI || window.location.origin,
  response_type: process.env.REACT_APP_COGNITO_RESPONSE_TYPE || 'code',
  // Using only 'openid' scope which is always available. Add 'email' and 'profile' if enabled in Cognito
  scope: 'openid',
  onSigninCallback: (user: { access_token?: string } | void) => {
    // Save the access token to localStorage for API calls
    // Use access_token for API calls (not id_token)
    if (user?.access_token) {
      localStorage.setItem('accessToken', user.access_token);
      console.log('Access token stored');
    } else {
      console.warn('No access token in signin callback', user);
    }

    // Cognito can only return to one registered URI, so send the user on to
    // wherever they were headed. Matters most for checkout completion, where
    // the session_id in the query string is the only handle on a paid order.
    const destination = consumePostLoginDestination();
    if (destination) {
      window.location.replace(destination);
      return;
    }

    // Remove the query params from URL after successful login
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <HelmetProvider>
      <AuthProvider {...cognitoAuthConfig}>
        <App />
      </AuthProvider>
    </HelmetProvider>
  </React.StrictMode>
);

